import { useEffect, useRef, useState, useContext } from 'react';
import { BackendContext } from '../../Context/BackendContext.js';
import { Spinner } from "@heroui/react";

const CANVAS_SIZE = 512;

// Switch to "cover" if you prefer filling the square with crop.
const FIT_MODE = 'contain'; // 'contain' | 'cover'

export default function InventoryArtwork({ artwork, onClick }) {
  const canvasRef = useRef(null);
  const backendContext = useContext(BackendContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchAndRenderArtwork = async () => {
      setLoading(true);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      // Ensure a true 512x512 backing store (not just CSS)
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;

      // Background (white); change if you prefer transparent
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      // Pixel-art friendly
      ctx.imageSmoothingEnabled = false;
      ctx.imageSmoothingQuality = 'low';

      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        const srcW = img.width;
        const srcH = img.height;

        if (!srcW || !srcH) {
          setLoading(false);
          return;
        }

        // Compute scale
        const containScale = Math.min(CANVAS_SIZE / srcW, CANVAS_SIZE / srcH);
        const coverScale = Math.max(CANVAS_SIZE / srcW, CANVAS_SIZE / srcH);

        // For pixel art: integer upscale when image is smaller than canvas.
        const chooseScale = (rawScale) => {
          const isUpscale = srcW <= CANVAS_SIZE && srcH <= CANVAS_SIZE;
          if (isUpscale) {
            const intScale = Math.max(1, Math.floor(rawScale));
            return intScale;
          }
          // downscaling or mixed: use the raw (can be fractional)
          return rawScale;
        };

        if (FIT_MODE === 'cover') {
          // Fill canvas fully (may crop)
          const scale = chooseScale(coverScale);
          const drawW = srcW * scale;
          const drawH = srcH * scale;

          // Compute source rect to crop so the scaled image exactly fits 512x512
          const srcCropW = CANVAS_SIZE / scale;
          const srcCropH = CANVAS_SIZE / scale;
          const sx = Math.max(0, (srcW - srcCropW) / 2);
          const sy = Math.max(0, (srcH - srcCropH) / 2);

          ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          ctx.drawImage(
            img,
            sx, sy, srcCropW, srcCropH,
            0, 0, CANVAS_SIZE, CANVAS_SIZE
          );
        } else {
          // Default: contain (no crop), centered, may letterbox
          const scale = chooseScale(containScale);
          const destW = Math.round(srcW * scale);
          const destH = Math.round(srcH * scale);
          const dx = Math.floor((CANVAS_SIZE - destW) / 2);
          const dy = Math.floor((CANVAS_SIZE - destH) / 2);

          ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          ctx.drawImage(img, 0, 0, srcW, srcH, dx, dy, destW, destH);
        }

        setLoading(false);
      };

      img.onerror = () => {
        if (!cancelled) {
          console.error('Failed to load artwork image.');
          setLoading(false);
        }
      };

      img.src = `data:image/png;base64,${artwork.imageData}`;
    };

    fetchAndRenderArtwork();
    return () => { cancelled = true; };
  }, [artwork, backendContext]);

  return (
    <div className="relative w-[512px] h-[512px]" onClick={() => onClick(artwork)}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10 rounded-lg">
          <Spinner color="primary" size="lg" />
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ imageRendering: 'pixelated', border: '1px solid black' }}
        className="rounded-lg"
      />
    </div>
  );
}
